import { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'

import {
  ConditionFeatureFlag,
  ConditionStatementBreakpoint,
  ConditionTextOperator,
  ConditionValue,
} from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'

import { VariableWrapper } from '../VariableSelectV2/VariableWrapper'
import { useConditionRepeaterContext } from './ConditionRepeaterContext'
import { useConditionSelectContext } from './ConditionSelectContext'

type BreakpointOption = {
  flag: ConditionFeatureFlag.Breakpoint
  label: string
  icon: string
  value: string
}

const BREAKPOINT_OPTIONS: readonly BreakpointOption[] = [
  {
    flag: ConditionFeatureFlag.Breakpoint,
    label: 'Mobile Portrait',
    icon: 'phone_android',
    value: 'mobile-portrait',
  },
  {
    flag: ConditionFeatureFlag.Breakpoint,
    label: 'Mobile Landscape',
    icon: 'width_wide',
    value: 'mobile-landscape',
  },
  {
    flag: ConditionFeatureFlag.Breakpoint,
    label: 'Tablet Portrait',
    icon: 'tablet_android',
    value: 'tablet-portrait',
  },
  {
    flag: ConditionFeatureFlag.Breakpoint,
    label: 'Tablet Landscape',
    icon: 'float_landscape_2',
    value: 'tablet-landscape',
  },
  {
    flag: ConditionFeatureFlag.Breakpoint,
    label: 'Desktop',
    icon: 'desktop_mac',
    value: 'desktop',
  },
  {
    flag: ConditionFeatureFlag.Breakpoint,
    label: 'TV',
    icon: 'tv',
    value: 'tv',
  },
] as const

const BREAKPOINT_LOOKUP = new Map(BREAKPOINT_OPTIONS.map((option) => [option.value, option]))

const updateCondition = (
  value: ConditionValue,
  index: number,
  key: 'lhs' | 'rhs',
  flag: ConditionFeatureFlag,
  breakpointValue?: string
) => {
  const nextCond = [...value.conditions]
  const isSame = !key.includes('rhs') ? nextCond[index].lhs.flag === ConditionFeatureFlag.Breakpoint : true
  const nextOp = isSame ? nextCond[index].operator : ConditionTextOperator.IsEqual

  nextCond[index] = {
    ...nextCond[index],
    [key]: {
      flag,
      ...(breakpointValue && { value: breakpointValue }),
    },
    operator: nextOp,
  }

  return nextCond
}

export function ConditionBreakpointDD() {
  const { id, setOpen, isRhs, index } = useConditionSelectContext()
  const { onChange, value } = useConditionRepeaterContext()
  const [show, setShow] = useBoolean(false)
  const key = isRhs ? 'rhs' : 'lhs'

  const handleBreakpointSelect = (breakpointValue?: any) => {
    const nextCond = updateCondition(value, index, key, ConditionFeatureFlag.Breakpoint, breakpointValue)
    onChange?.({ isAny: value.isAny, conditions: nextCond })
    setShow(false)
    setOpen(false)
  }

  const renderBreakpointOptions = () => (
    <div className='flex flex-col'>
      {BREAKPOINT_OPTIONS.map((item) => (
        <button key={item.value} className='gap-2 sdd-item' onClick={() => handleBreakpointSelect(item.value)}>
          <MaterialIcon icon={item.icon} /> {item.label}
        </button>
      ))}
    </div>
  )

  if (isRhs) {
    return (
      <VariableWrapper
        id={id ? `${id}-breakpoint` : undefined}
        show={show}
        setShow={setShow}
        title={<Trans>Screen Size</Trans>}
        icon={<MaterialIcon icon='responsive_layout' />}
      >
        {renderBreakpointOptions()}
      </VariableWrapper>
    )
  }

  return (
    <div
      id={id ? `${id}-breakpoint-id` : undefined}
      onClick={() => handleBreakpointSelect()}
      className='flex-c w-full sdd-item'
    >
      <div className='flex-c w-full pointer-events-none'>
        <MaterialIcon icon='responsive_layout' size={16} />
        <div className='pointer-events-none'>
          <Trans>Screen Size</Trans>
        </div>
      </div>
    </div>
  )
}

export function ConditionBreakpointStatement({ statement }: { statement: ConditionStatementBreakpoint }) {
  const breakpointOption = useMemo(() => BREAKPOINT_LOOKUP.get(statement.value || ''), [statement.value])
  return (
    <div className='flex-c min-w-0'>
      <div className='flex-c whitespace-nowrap'>
        <MaterialIcon icon='responsive_layout' />
        <Trans>{breakpointOption?.label || 'Screen Size'}</Trans>
      </div>
    </div>
  )
}
